import { useState, useEffect, useCallback } from 'react'
import { Filter } from '../components/SubHeaderFilter/types'
import { useUserPreferences } from '../contexts/UserPreferencesContext'

export function usePersistedFilters(key: string) {
  const { getPreference, setPreference, isLoading: isPrefsLoading } = useUserPreferences()
  const [appliedFilters, setAppliedFilters] = useState<Filter[]>([])

  useEffect(() => {
    if (isPrefsLoading) return
    const saved = getPreference<Filter[] | undefined>(`filters_${key}`)
    if (saved) {
      setAppliedFilters(saved)
    }
  }, [isPrefsLoading, key, getPreference])

  const setAndPersistFilters = useCallback((filters: Filter[] | ((prev: Filter[]) => Filter[])) => {
    setAppliedFilters((prev) => {
      const next = typeof filters === 'function' ? filters(prev) : filters
      setPreference(`filters_${key}`, next)
      return next
    })
  }, [key, setPreference])

  const handleApplyFilter = useCallback((newFilter: Filter) => {
    setAndPersistFilters((prev) => {
      const exists = prev.some(
        (f) => f.key === newFilter.key && f.filterType === newFilter.filterType && f.value === newFilter.value
      )
      if (exists) return prev
      return [...prev, newFilter]
    })
  }, [setAndPersistFilters])

  const handleReplaceFilter = useCallback((newFilter: Filter) => {
    setAndPersistFilters((prev) => [...prev.filter((f) => f.key !== newFilter.key), newFilter])
  }, [setAndPersistFilters])

  const handleRemoveFilter = useCallback((index: number) => {
    setAndPersistFilters((prev) => prev.filter((_, i) => i !== index))
  }, [setAndPersistFilters])

  const handleClearAll = useCallback(() => {
    setAndPersistFilters([])
  }, [setAndPersistFilters])

  return {
    appliedFilters,
    handleApplyFilter,
    handleReplaceFilter,
    isPrefsLoading,
    handleRemoveFilter,
    handleClearAll,
  }
}
